import { SlideDeck, SlideDeckStatus, SlideType } from '@core/swagger-client';
import { Chapter, ChatMessage, Poll, VideoLayout } from '@core/types';
import {
    ActivePollStatus,
    OnDemandTimelineActivePollChangedEvent,
    OnDemandTimelineActivePollVotesChangedEvent,
    OnDemandTimelineChapterChangedEvent,
    OnDemandTimelineChaptersChangedEvent,
    OnDemandTimelineChatMessageEvent,
    OnDemandTimelineCutsEvent,
    OnDemandTimelineEvent,
    OnDemandTimelinePollsChangedEvent,
    OnDemandTimelineSlideChangedEvent,
    OnDemandTimelineSlideDeckChangedEvent,
    OnDemandTimelineVideoLayoutChangedEvent,
    parseTimespan,
    toTimespan
} from '@online-webinar/store';
import { cloneDeep } from 'lodash-es';
import { Duration } from 'luxon';

export interface BaseTimelineEvent {
    changed: boolean;
    timestamp: Duration;
}

export interface VideoLayoutTimelineEvent extends BaseTimelineEvent {
    layout: VideoLayout;
}

export interface SlideTimelineEvent extends BaseTimelineEvent {
    url: string;
}

export interface ChatTimelineEvent extends BaseTimelineEvent {
    message: ChatMessage;
}

export interface PollTimelineEvent extends BaseTimelineEvent {
    poll: Poll;
    duration: Duration;
    originalDuration: Duration;
    votes: PollVoteTimelineEvent[];
    statuses: PollStatusEvent[];
}

export interface PollVoteTimelineEvent {
    votes: { [optionId: string]: number } | { [optionId: string]: { votes: number; weightedVotes: number } };
    timestamp: Duration;
}

export interface PollStatusEvent {
    status: ActivePollStatus;
    timestamp: Duration;
}

export interface ChapterTimelineEvent extends BaseTimelineEvent {
    title: string;
}

export interface CutTimelineEvent extends BaseTimelineEvent {
    duration: Duration;
    desiredDuration: Duration;
}

export interface Timeline {
    changed: boolean;

    webinarId: string;
    duration?: Duration;

    slides: SlideTimelineEvent[];
    chat: ChatTimelineEvent[];
    polls: PollTimelineEvent[];
    chapters: ChapterTimelineEvent[];
    cuts: CutTimelineEvent[];
    keyframes: Duration[];
    videoLayouts: VideoLayoutTimelineEvent[];
}

/** Polls must stay open for at least this long, otherwise they collapse to a zero-width, uneditable sliver. */
export const MIN_POLL_DURATION = Duration.fromObject({ seconds: 15 });

export const alignDurationToKeyframe = (timeline: Timeline, timestamp: Duration, duration: Duration): Duration => {
    if (timeline.keyframes.length === 0) {
        return duration;
    }

    const end = timestamp.plus(duration);
    const endKeyframe = alignToKeyframe(timeline, end);
    return endKeyframe.minus(timestamp);
};

export const alignToKeyframe = (timeline: Timeline, timestamp: Duration): Duration => {
    if (timeline.keyframes.length === 0) {
        return timestamp;
    }

    const index = timeline.keyframes.findIndex((k) => k.as('seconds') > timestamp.as('seconds'));
    if (index === -1) {
        return timeline.duration ?? timeline.keyframes[timeline.keyframes.length - 1];
    }

    const prev = timeline.keyframes[index - 1] || Duration.fromMillis(0);
    const next = timeline.keyframes[index];
    const diffPrev = timestamp.as('seconds') - prev.as('seconds');
    const diffNext = next.as('seconds') - timestamp.as('seconds');
    return diffPrev < diffNext ? prev : next;
};

/** Convert OnDemandTimelineEvent's into a Timeline object */
export const parseOnDemandEvents = (timeline: OnDemandTimelineEvent[]): Timeline => {
    let webinarId = '';
    if (timeline.length > 0) {
        webinarId = timeline[0].event.webinarId;
    }

    const slides = parseSlides(timeline);
    const chat = parseChat(timeline);
    const polls = parsePolls(timeline);
    const chapters = parseChapters(timeline);
    const cuts = parseCuts(timeline);
    const videoLayouts = parseVideoLayouts(timeline);

    return { changed: false, webinarId, slides, chat, polls, chapters, cuts, videoLayouts, keyframes: [] };
};

const parseSlides = (timeline: OnDemandTimelineEvent[]): SlideTimelineEvent[] => {
    let slideNumber = 0;
    let slideDeck: SlideDeck | null = null;
    const events: SlideTimelineEvent[] = [];
    for (let i = 0; i < timeline.length; i++) {
        const event = timeline[i];
        if (event.eventType === 'SlideDeckChangedEvent') {
            slideDeck = (event.event as OnDemandTimelineSlideDeckChangedEvent).slideDeck;
        } else if (event.eventType === 'SlideChangedEvent') {
            slideNumber = (event.event as OnDemandTimelineSlideChangedEvent).slideNumber;
        } else {
            continue;
        }

        if (slideDeck) {
            const slide = slideDeck.slides[slideNumber];
            if (!slide) {
                continue;
            }

            const timestamp = Duration.fromISOTime(event.timestamp);
            const lastSlide = events.length > 0 ? events[events.length - 1] : null;

            // If the last slide is the same as the current slide, skip it
            if (lastSlide && lastSlide.url === slide.url) {
                continue;
            }

            // Overwrite last slide with the same timestamp
            if (lastSlide && lastSlide.timestamp.equals(timestamp)) {
                lastSlide.url = slide.url;
                continue;
            }

            events.push({
                url: slide.url,
                timestamp: timestamp,
                changed: false
            });
        }
    }

    return events;
};

const parseChat = (timeline: OnDemandTimelineEvent[]): ChatTimelineEvent[] => {
    const events: ChatTimelineEvent[] = [];
    for (let i = 0; i < timeline.length; i++) {
        const event = timeline[i];
        if (event.eventType === 'ChatMessageEvent') {
            const chatMessage = (event.event as OnDemandTimelineChatMessageEvent).chatMessage;
            events.push({
                message: cloneDeep(chatMessage),
                timestamp: Duration.fromISOTime(event.timestamp),
                changed: false
            });
        }
    }
    return events;
};

const parsePolls = (timeline: OnDemandTimelineEvent[]): PollTimelineEvent[] => {
    let polls: Poll[] = [];
    const events: PollTimelineEvent[] = [];
    let activePollEvent: PollTimelineEvent | null = null;
    for (let i = 0; i < timeline.length; i++) {
        const event = timeline[i];
        if (event.eventType === 'PollsChangedEvent') {
            polls = (event.event as OnDemandTimelinePollsChangedEvent).polls;
        }

        if (event.eventType === 'ActivePollChangedEvent') {
            const activeEvent = event.event as OnDemandTimelineActivePollChangedEvent;
            if (activeEvent.activePollStatus === ActivePollStatus.Inactive) {
                // The deactivation marks the real end of the poll, so it determines the duration.
                // Without this a poll that received no votes after activation keeps duration 0.
                if (activePollEvent) {
                    const timestamp = Duration.fromObject({ seconds: parseTimespan(event.timestamp) - activePollEvent.timestamp.as('seconds') });
                    activePollEvent.duration = activePollEvent.originalDuration = timestamp;
                }
                activePollEvent = null;
                continue;
            }
            if (!activePollEvent || activePollEvent.poll.pollId !== activeEvent.activePollId) {
                const poll = polls.find((p) => p.pollId === activeEvent.activePollId);
                if (!poll) {
                    continue;
                }
                activePollEvent = {
                    changed: false,
                    timestamp: Duration.fromISOTime(event.timestamp),
                    poll: poll,
                    duration: Duration.fromMillis(0),
                    originalDuration: Duration.fromMillis(0),
                    votes: [],
                    statuses: []
                };
                events.push(activePollEvent);
            }

            const timestamp = Duration.fromObject({ seconds: parseTimespan(event.timestamp) - activePollEvent.timestamp.as('seconds') });
            activePollEvent.duration = activePollEvent.originalDuration = timestamp;
            activePollEvent.statuses.push({
                status: activeEvent.activePollStatus,
                timestamp
            });
        }

        if (event.eventType === 'ActivePollVotesChangedEvent') {
            const activeVotesEvent = event.event as OnDemandTimelineActivePollVotesChangedEvent;
            if (!activePollEvent || activePollEvent.poll.pollId !== activeVotesEvent.pollId) {
                continue;
            }

            const timestamp = Duration.fromObject({ seconds: parseTimespan(event.timestamp) - activePollEvent.timestamp.as('seconds') });
            activePollEvent.duration = activePollEvent.originalDuration = timestamp;
            activePollEvent.votes.push({
                votes: activeVotesEvent.results,
                timestamp
            });
        }
    }

    // Guarantee a usable minimum duration so polls never collapse to a zero-width, uneditable sliver.
    // duration and originalDuration are kept in sync so the save-time scale factor stays 1.
    for (const event of events) {
        if (event.duration.toMillis() < MIN_POLL_DURATION.toMillis()) {
            event.duration = event.originalDuration = MIN_POLL_DURATION;
        }
    }

    return events;
};

const parseChapters = (timeline: OnDemandTimelineEvent[]): ChapterTimelineEvent[] => {
    let chapters: Chapter[] = [];
    const events: ChapterTimelineEvent[] = [];
    for (let i = 0; i < timeline.length; i++) {
        const event = timeline[i];
        if (event.eventType === 'ChaptersChangedEvent') {
            chapters = (event.event as OnDemandTimelineChaptersChangedEvent).chapters;
        }

        if (event.eventType === 'ChapterChangedEvent') {
            const id = (event.event as OnDemandTimelineChapterChangedEvent).chapterId;
            const title = chapters.find((c) => c.chapterId === id)?.chapterTitle;
            if (!title) {
                continue;
            }

            events.push({
                changed: false,
                timestamp: Duration.fromISOTime(event.timestamp),
                title
            });
        }
    }
    return events;
};

const parseCuts = (timeline: OnDemandTimelineEvent[]): CutTimelineEvent[] => {
    for (let i = 0; i < timeline.length; i++) {
        const event = timeline[i];
        if (event.eventType === 'CutsEvent') {
            return (event.event as OnDemandTimelineCutsEvent).cuts.map((c) => ({
                changed: false,
                timestamp: Duration.fromObject({ seconds: parseTimespan(c.start) }),
                duration: Duration.fromObject({ seconds: parseTimespan(c.end) - parseTimespan(c.start) }),
                desiredDuration: Duration.fromObject({ seconds: parseTimespan(c.end) - parseTimespan(c.start) })
            }));
        }
    }
    return [];
};

const parseVideoLayouts = (timeline: OnDemandTimelineEvent[]): VideoLayoutTimelineEvent[] => {
    const layouts: VideoLayoutTimelineEvent[] = [];
    for (let i = 0; i < timeline.length; i++) {
        const event = timeline[i];
        if (event.eventType === 'VideoLayoutChangedEvent') {
            const layout = event.event as OnDemandTimelineVideoLayoutChangedEvent;
            layouts.push({
                changed: false,
                timestamp: Duration.fromISOTime(event.timestamp),
                layout: layout.videoLayout
            });
        }
    }
    return layouts;
};

/** Convert a Timeline object into OnDemandTimelineEvent's  */
export const parseTimeline = (timeline: Timeline): OnDemandTimelineEvent[] => {
    let events: OnDemandTimelineEvent[] = [];
    events = events.concat(parseSlideEvents(timeline));
    events = events.concat(parseChatEvents(timeline));
    events = events.concat(parsePollEvents(timeline));
    events = events.concat(parseChapterEvents(timeline));
    events = events.concat(parseCutEvents(timeline));
    events = events.concat(parseVideoLayoutEvents(timeline));
    return events.sort((a, b) => parseTimespan(a.timestamp) - parseTimespan(b.timestamp));
};

const parseSlideEvents = (timeline: Timeline): OnDemandTimelineEvent[] => {
    let events: OnDemandTimelineEvent[] = [];

    const slideDeck: SlideDeck = {
        status: SlideDeckStatus.Ready,
        filename: 'slides-edited.pdf',
        slides: timeline.slides.map((s) => ({ url: s.url, type: SlideType.Image })),
        totalSlides: timeline.slides.length
    };
    events.push({
        eventType: 'SlideDeckChangedEvent',
        timestamp: '00:00:00',
        event: <OnDemandTimelineSlideDeckChangedEvent>{
            timestamp: '00:00:00',
            webinarId: timeline.webinarId,
            slideDeck
        }
    });
    for (let i = 0; i < timeline.slides.length; i++) {
        const slide = timeline.slides[i];
        events.push({
            eventType: 'SlideChangedEvent',
            timestamp: toTimespan(slide.timestamp),
            event: <OnDemandTimelineSlideChangedEvent>{
                webinarId: timeline.webinarId,
                timestamp: toTimespan(slide.timestamp),
                slideNumber: i
            }
        });
    }

    return events;
};

const parseChatEvents = (timeline: Timeline): OnDemandTimelineEvent[] => {
    let events: OnDemandTimelineEvent[] = [];
    for (let i = 0; i < timeline.chat.length; i++) {
        const chat = timeline.chat[i];
        events.push({
            eventType: 'ChatMessageEvent',
            timestamp: toTimespan(chat.timestamp),
            event: <OnDemandTimelineChatMessageEvent>{
                webinarId: timeline.webinarId,
                chatMessage: chat.message
            }
        });
    }
    return events;
};

const parsePollEvents = (timeline: Timeline): OnDemandTimelineEvent[] => {
    let events: OnDemandTimelineEvent[] = [];
    let polls = timeline.polls.map((p) => p.poll);
    // Remove duplicate polls
    polls = polls.filter((p, i) => polls.findIndex((p2) => p2.pollId === p.pollId) === i);
    events.push({
        eventType: 'PollsChangedEvent',
        timestamp: '00:00:00',
        event: <OnDemandTimelinePollsChangedEvent>{
            timestamp: '00:00:00',
            webinarId: timeline.webinarId,
            polls
        }
    });

    const scaleToTimespan = (base: Duration, timestamp: Duration, scale: number): string => {
        return toTimespan(Duration.fromObject({ seconds: Math.round(timestamp.plus(base).as('seconds') * scale) }));
    };

    for (let i = 0; i < timeline.polls.length; i++) {
        const poll = timeline.polls[i];
        const scale = poll.originalDuration.as('seconds') > 0 ? poll.duration.as('seconds') / poll.originalDuration.as('seconds') : 1;
        for (let j = 0; j < poll.statuses.length; j++) {
            const status = poll.statuses[j];
            events.push({
                eventType: 'ActivePollChangedEvent',
                timestamp: scaleToTimespan(poll.timestamp, status.timestamp, scale),
                event: <OnDemandTimelineActivePollChangedEvent>{
                    timestamp: scaleToTimespan(poll.timestamp, status.timestamp, scale),
                    webinarId: timeline.webinarId,
                    activePollId: poll.poll.pollId,
                    activePollStatus: status.status
                }
            });
        }
        for (let j = 0; j < poll.votes.length; j++) {
            const vote = poll.votes[j];
            events.push({
                eventType: 'ActivePollVotesChangedEvent',
                timestamp: scaleToTimespan(poll.timestamp, vote.timestamp, scale),
                event: <OnDemandTimelineActivePollVotesChangedEvent>{
                    timestamp: scaleToTimespan(poll.timestamp, vote.timestamp, scale),
                    webinarId: timeline.webinarId,
                    pollId: poll.poll.pollId,
                    results: vote.votes
                }
            });
        }

        // Inactive isn't part of "statuses"
        events.push({
            eventType: 'ActivePollChangedEvent',
            timestamp: toTimespan(poll.timestamp.plus(poll.duration)),
            event: <OnDemandTimelineActivePollChangedEvent>{
                timestamp: toTimespan(poll.timestamp.plus(poll.duration)),
                webinarId: timeline.webinarId,
                activePollId: '00000000-0000-0000-0000-000000000000',
                activePollStatus: ActivePollStatus.Inactive
            }
        });
    }
    return events;
};

const parseChapterEvents = (timeline: Timeline): OnDemandTimelineEvent[] => {
    let events: OnDemandTimelineEvent[] = [];

    const chapters = timeline.chapters.map((c, i) => ({ chapterId: i.toString(), chapterTitle: c.title }));
    events.push({
        eventType: 'ChaptersChangedEvent',
        timestamp: '00:00:00',
        event: <OnDemandTimelineChaptersChangedEvent>{
            timestamp: '00:00:00',
            webinarId: timeline.webinarId,
            chapters
        }
    });
    for (let i = 0; i < timeline.chapters.length; i++) {
        const chapter = timeline.chapters[i];
        events.push({
            eventType: 'ChapterChangedEvent',
            timestamp: toTimespan(chapter.timestamp),
            event: <OnDemandTimelineChapterChangedEvent>{
                webinarId: timeline.webinarId,
                timestamp: toTimespan(chapter.timestamp),
                chapterId: i.toString()
            }
        });
    }
    return events;
};

const parseCutEvents = (timeline: Timeline): OnDemandTimelineEvent[] => {
    return [
        {
            eventType: 'CutsEvent',
            timestamp: '00:00:00',
            event: <OnDemandTimelineCutsEvent>{
                timestamp: '00:00:00',
                webinarId: timeline.webinarId,
                cuts: timeline.cuts.map((c) => ({
                    start: toTimespan(c.timestamp),
                    end: toTimespan(c.duration.plus(c.timestamp))
                }))
            }
        }
    ];
};

const parseVideoLayoutEvents = (timeline: Timeline): OnDemandTimelineEvent[] => {
    const events: OnDemandTimelineEvent[] = [];
    for (let i = 0; i < timeline.videoLayouts.length; i++) {
        const layout = timeline.videoLayouts[i];
        events.push({
            eventType: 'VideoLayoutChangedEvent',
            timestamp: toTimespan(layout.timestamp),
            event: <OnDemandTimelineVideoLayoutChangedEvent>{
                webinarId: timeline.webinarId,
                timestamp: toTimespan(layout.timestamp),
                videoLayout: layout.layout
            }
        });
    }
    return events;
};
